import { Box, Grid, Alert, AlertIcon } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import useEscopoFornecedor, {
  IEscopoDiaFilters
} from '@hook/mrp/use-escopo-fornecedor'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'
import EscopoTable from '@component/Modules/ConfigPurchase/Escopo/EscopoTable'

const TIPO_OPTIONS = [
  { value: 0, label: 'Regular' },
  { value: 1, label: 'Atraso' },
  { value: 2, label: 'Pendência' }
]

// Datas no fuso local (toISOString usa UTC e à noite "pula" para o dia seguinte)
const today = () => dayjs().format('YYYY-MM-DD')
const daysAgo = (n: number) => dayjs().subtract(n, 'day').format('YYYY-MM-DD')

const EscopoConfigPurchase: FC<{ empresaId?: string }> = ({ empresaId }) => {
  const { menuToggle } = useLayout()
  const { hasSessions } = useSessions()
  const { getFilters } = useFilters()
  const { data, loading, error, pagination, currentPage, fetchData, goToPage } =
    useEscopoFornecedor()

  const [filters, setFilters] = useState(true)
  const [params, setParams] = useState<any>({})

  // Datas do período (gerenciadas separadamente para o defaultDate do LayoutFilters)
  const [initialDate, setInitialDate] = useState(() => daysAgo(30))
  const [finalDate, setFinalDate] = useState(today)

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '90vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 442px)',
    [menuToggle, hasSessions]
  )

  const filtersList = useMemo(
    () => [
      {
        id: 'periodo',
        label: 'Período',
        type: 'date-input' as const,
        isRequired: true,
        isDisabled: false
      },
      {
        id: 'companies',
        label: 'Empresas',
        type: 'select-pagination' as const,
        filterId: 'companies' as const,
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'centers',
        label: 'Centros',
        type: 'select-pagination' as const,
        filterId: 'centers' as const,
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'providersForPurchase',
        label: 'Fornecedores',
        type: 'select-pagination' as const,
        filterId: 'providersForPurchase' as const,
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'compactCategories',
        label: 'Categorias',
        type: 'select-pagination' as const,
        filterId: 'compactCategories' as const,
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'tipos',
        label: 'Tipo',
        type: 'select' as const,
        options: TIPO_OPTIONS,
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'compradores',
        label: 'Compradores',
        type: 'select-pagination' as const,
        filterId: 'compradores' as const,
        isMulti: true,
        isRequired: false,
        isDisabled: false
      }
    ],
    []
  )

  const handleUpdateParams = useCallback(
    ({ name, value }: { name: string; value: any }) => {
      // date-input emite com name 'initialDate' e 'finalDate'
      if (name === 'initialDate') {
        setInitialDate(value)
        return
      }
      if (name === 'finalDate') {
        setFinalDate(value)
        return
      }
      setParams((prev: any) => ({
        ...prev,
        [name]: value
      }))
    },
    []
  )

  const clearAllFilters = useCallback(() => {
    setParams({})
    setInitialDate(daysAgo(30))
    setFinalDate(today())
  }, [])

  // Sem empresa escolhida no filtro lateral, usa a empresa selecionada no topo da tela
  const empresaPadrao = useMemo(
    () => (empresaId && !isNaN(Number(empresaId)) ? [Number(empresaId)] : []),
    [empresaId]
  )

  const handleFilter = useCallback(() => {
    const f: IEscopoDiaFilters = {
      dataInicio: initialDate ? `${initialDate}T00:00:00` : '',
      dataFim: finalDate ? `${finalDate}T00:00:00` : '',
      empresasIds: params.companies?.length
        ? params.companies.map((e: any) => e.value)
        : empresaPadrao,
      lojasIds: params.centers?.map((c: any) => c.value) || [],
      fornecedoresIds:
        params.providersForPurchase?.map((f: any) => f.value) || [],
      categoriasCompactasIds:
        params.compactCategories?.map((c: any) => c.value) || [],
      tipos: params.tipos?.map((t: any) => t.value) || [],
      compradores: params.compradores?.map((c: any) => c.value) || []
    }
    fetchData(f, 1)
  }, [initialDate, finalDate, params, fetchData, empresaPadrao])

  useEffect(() => {
    getFilters({
      companies: true,
      centers: true,
      providersForPurchase: true,
      compactCategories: true,
      compradores: true
    })
  }, [getFilters])

  // Auto-filtrar ao abrir a tela e ao trocar a empresa do topo, com período padrão (últimos 30 dias)
  useEffect(() => {
    const autoFilters: IEscopoDiaFilters = {
      dataInicio: `${daysAgo(30)}T00:00:00`,
      dataFim: `${today()}T00:00:00`,
      empresasIds: empresaPadrao
    }
    fetchData(autoFilters, 1)
  }, [empresaPadrao, fetchData])

  return (
    <Grid
      bg="white"
      templateColumns={filters ? '260px calc(100vw - 280px)' : '100%'}
    >
      {filters && (
        <LayoutFilters
          filters={filtersList}
          values={params}
          defaultDate={{
            initialDate,
            finalDate
          }}
          onChange={handleUpdateParams}
          onFilter={handleFilter}
          onClearAllFilters={clearAllFilters}
          loading={loading}
          canCancel={false}
          heightAll={height}
        />
      )}

      <Box>
        <LayoutTools
          title="Escopo de Compra do Dia"
          filters={filters}
          toggleFilters={() => setFilters(prev => !prev)}
          isDisabled={loading || data.length === 0}
        />

        {loading ? (
          <LayoutLoader
            title="Carregando escopo..."
            message="Aguarde enquanto buscamos os dados"
          />
        ) : error ? (
          <Alert status="error" borderRadius="md" fontSize="sm" m={4}>
            <AlertIcon />
            {error}
          </Alert>
        ) : (
          <EscopoTable
            data={data}
            pagination={pagination}
            currentPage={currentPage}
            onPageChange={goToPage}
          />
        )}
      </Box>
    </Grid>
  )
}

export default EscopoConfigPurchase
